import React, { useState, useMemo } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import DateInput from './DateInput';

const CHANGE_TYPES = [
  { value: 'full_to_part_time', label: 'Full-Time to Part-Time' },
  { value: 'part_to_full_time', label: 'Part-Time to Full-Time' },
  { value: 'salary_change', label: 'Salary Change' },
  { value: 'hours_change', label: 'Change of Hours' },
  { value: 'salaried_to_hourly', label: 'Salaried to Hourly' },
  { value: 'hourly_to_salaried', label: 'Hourly to Salaried' },
  { value: 'other', label: 'Other' },
];

export default function ContractualChangeModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    change_type: 'full_to_part_time',
    effective_date: '',
    new_annual_salary: '',
    new_days_per_week: '',
    new_hours_per_day: '',
    new_weekly_hours_custom: '',
    fte_weekly_hours: '37.5',
    new_hourly_rate: '',
    new_contracted_hours: '',
    notes: '',
  });
  const [hoursMode, setHoursMode] = useState('days'); // 'days' or 'custom'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Auto-calculated fields for "days" mode
  const weeklyHoursFromDays = useMemo(() => {
    const days = parseFloat(form.new_days_per_week) || 0;
    const hpd = parseFloat(form.new_hours_per_day) || 0;
    return days * hpd;
  }, [form.new_days_per_week, form.new_hours_per_day]);

  // The effective weekly hours based on current mode
  const effectiveWeeklyHours = hoursMode === 'days'
    ? weeklyHoursFromDays
    : parseFloat(form.new_weekly_hours_custom) || 0;

  const fteHours = parseFloat(form.fte_weekly_hours) || 0;

  // Pro rata: based on hours ratio when in custom mode, days ratio when in days mode
  const proRataSalary = useMemo(() => {
    const annual = parseFloat(form.new_annual_salary) || 0;
    if (annual <= 0) return '';

    if (hoursMode === 'days') {
      const days = parseFloat(form.new_days_per_week) || 0;
      if (days > 0 && days <= 5) {
        return (annual * (days / 5)).toFixed(2);
      }
    } else {
      const custom = parseFloat(form.new_weekly_hours_custom) || 0;
      const fte = parseFloat(form.fte_weekly_hours) || 0;
      if (custom > 0 && fte > 0) {
        return (annual * (custom / fte)).toFixed(2);
      }
    }
    return '';
  }, [form.new_annual_salary, form.new_days_per_week, form.new_weekly_hours_custom, form.fte_weekly_hours, hoursMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.change_type) {
      setError('Please select a change type');
      return;
    }
    if (!form.effective_date) {
      setError('Please enter an effective date');
      return;
    }

    const isWorkingPattern = ['full_to_part_time', 'hours_change'].includes(form.change_type);

    if (isWorkingPattern) {
      if (hoursMode === 'days') {
        if (!form.new_days_per_week || parseFloat(form.new_days_per_week) <= 0) {
          setError('Please enter the number of days per week');
          return;
        }
        if (parseFloat(form.new_days_per_week) > 7) {
          setError('Days per week cannot exceed 7');
          return;
        }
        if (!form.new_hours_per_day || parseFloat(form.new_hours_per_day) <= 0) {
          setError('Please enter the hours per day');
          return;
        }
        if (parseFloat(form.new_hours_per_day) > 24) {
          setError('Hours per day cannot exceed 24');
          return;
        }
      } else {
        if (!form.new_weekly_hours_custom || parseFloat(form.new_weekly_hours_custom) <= 0) {
          setError('Please enter the new weekly hours');
          return;
        }
        if (!form.fte_weekly_hours || parseFloat(form.fte_weekly_hours) <= 0) {
          setError('Please enter the full-time weekly hours');
          return;
        }
      }
    }

    if (form.change_type === 'full_to_part_time' && !form.new_annual_salary) {
      setError('Please enter the new annual salary (FTE)');
      return;
    }
    if (form.new_annual_salary && parseFloat(form.new_annual_salary) <= 0) {
      setError('Salary must be greater than £0.00');
      return;
    }
    if (form.change_type === 'salaried_to_hourly') {
      if (!form.new_hourly_rate || parseFloat(form.new_hourly_rate) <= 0) {
        setError('Please enter the hourly rate');
        return;
      }
      if (form.new_contracted_hours === '' || form.new_contracted_hours === undefined) {
        setError('Please enter the contracted hours per week (0 for zero-hours)');
        return;
      }
      if (parseFloat(form.new_contracted_hours) < 0) {
        setError('Contracted hours cannot be negative');
        return;
      }
    }
    if (form.change_type === 'hourly_to_salaried') {
      if (!form.new_annual_salary || parseFloat(form.new_annual_salary) <= 0) {
        setError('Please enter the new annual salary');
        return;
      }
    }

    setSaving(true);
    try {
      const saveData = {
        change_type: form.change_type,
        effective_date: form.effective_date,
        notes: form.notes,
        change_type_label: CHANGE_TYPES.find(t => t.value === form.change_type)?.label || form.change_type,
      };

      if (form.new_annual_salary) {
        saveData.new_annual_salary = form.new_annual_salary;
      }

      if (isWorkingPattern) {
        saveData.hours_mode = hoursMode;
        saveData.new_weekly_hours = effectiveWeeklyHours.toFixed(1);

        if (hoursMode === 'days') {
          saveData.new_days_per_week = form.new_days_per_week;
          saveData.new_hours_per_day = form.new_hours_per_day;
        } else {
          saveData.new_weekly_hours_custom = form.new_weekly_hours_custom;
          saveData.fte_weekly_hours = form.fte_weekly_hours;
        }
      }

      if (form.change_type === 'full_to_part_time' && proRataSalary) {
        saveData.pro_rata_salary = proRataSalary;
      }

      if (form.change_type === 'salaried_to_hourly') {
        saveData.new_hourly_rate = form.new_hourly_rate;
        saveData.new_contracted_hours = form.new_contracted_hours;
        saveData.new_pay_type = 'hourly';
      }
      if (form.change_type === 'hourly_to_salaried') {
        saveData.new_pay_type = 'salaried';
      }

      await onSave(employee.id, saveData);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const showWorkingPattern = ['full_to_part_time', 'hours_change'].includes(form.change_type);
  const showSalaryField = ['full_to_part_time', 'part_to_full_time', 'salary_change', 'hourly_to_salaried'].includes(form.change_type);
  const showHourlyFields = form.change_type === 'salaried_to_hourly';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full dark:bg-gray-800 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Contractual Change - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Change Type *</label>
              <select name="change_type" value={form.change_type} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                {CHANGE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {form.change_type === 'other' && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded p-2">
                  If {employee.first_name} is leaving the business, please use the <strong>Leaver</strong> action instead — &ldquo;Other&rdquo; contractual changes don&rsquo;t remove the employee from next month&rsquo;s payroll.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Effective Date *</label>
              <DateInput name="effective_date" value={form.effective_date} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>

            {showSalaryField && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {form.change_type === 'full_to_part_time' ? 'New Annual Salary (FTE) *' : 'New Annual Salary'}
                </label>
                <div className="mt-1 relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                  <input type="number" name="new_annual_salary" step="0.01" min="0" inputMode="decimal" value={form.new_annual_salary} onChange={handleChange}
                    className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="0.00" />
                </div>
                {form.change_type === 'full_to_part_time' && (
                  <p className="text-xs text-gray-500 mt-1">Enter the full-time equivalent salary. Pro rata will be calculated based on working pattern.</p>
                )}
              </div>
            )}

            {showWorkingPattern && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">New Working Pattern</p>
                    <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setHoursMode('days')}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          hoursMode === 'days'
                            ? 'bg-brand-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        Days &amp; Hours
                      </button>
                      <button
                        type="button"
                        onClick={() => setHoursMode('custom')}
                        className={`px-3 py-1 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                          hoursMode === 'custom'
                            ? 'bg-brand-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        Weekly Hours
                      </button>
                    </div>
                  </div>

                  {hoursMode === 'days' ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Days per Week *</label>
                          <input type="number" name="new_days_per_week" step="0.5" min="0.5" max="7" inputMode="decimal" value={form.new_days_per_week} onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="e.g. 3" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hours per Day *</label>
                          <input type="number" name="new_hours_per_day" step="0.25" min="0.25" max="24" inputMode="decimal" value={form.new_hours_per_day} onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="e.g. 7.5" />
                        </div>
                      </div>

                      {weeklyHoursFromDays > 0 && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-600">
                          <span className="font-medium">Weekly hours:</span> {weeklyHoursFromDays.toFixed(1)} hours
                          ({form.new_days_per_week} day{parseFloat(form.new_days_per_week) !== 1 ? 's' : ''} &times; {form.new_hours_per_day} hrs/day)
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Use this when the working pattern doesn't fit neatly into fixed days, e.g. 20 hours spread across the week.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Weekly Hours *</label>
                          <input type="number" name="new_weekly_hours_custom" step="0.5" min="0.5" max="168" inputMode="decimal" value={form.new_weekly_hours_custom} onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="e.g. 20" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">FT Weekly Hours *</label>
                          <input type="number" name="fte_weekly_hours" step="0.5" min="0.5" max="168" inputMode="decimal" value={form.fte_weekly_hours} onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="e.g. 37.5" />
                          <p className="text-xs text-gray-500 mt-1">Standard full-time hours for this role</p>
                        </div>
                      </div>

                      {effectiveWeeklyHours > 0 && fteHours > 0 && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-600">
                          <span className="font-medium">{effectiveWeeklyHours.toFixed(1)}</span> of <span className="font-medium">{fteHours.toFixed(1)}</span> hours
                          ({((effectiveWeeklyHours / fteHours) * 100).toFixed(0)}% of full-time)
                        </div>
                      )}
                    </>
                  )}

                  {form.change_type === 'full_to_part_time' && proRataSalary && (
                    <div className="text-sm text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/30 rounded p-3 border border-brand-200 dark:border-brand-800">
                      <span className="font-medium">Pro rata salary:</span> &pound;{parseFloat(proRataSalary).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="block text-xs mt-1 text-brand-600 dark:text-brand-500">
                        {hoursMode === 'days'
                          ? `Based on ${form.new_days_per_week} of 5 days (${((parseFloat(form.new_days_per_week) / 5) * 100).toFixed(0)}% of FTE)`
                          : `Based on ${form.new_weekly_hours_custom} of ${form.fte_weekly_hours} hours (${((effectiveWeeklyHours / fteHours) * 100).toFixed(0)}% of FTE)`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showHourlyFields && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg space-y-4 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Hourly Pay Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hourly Rate *</label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                        <input type="number" name="new_hourly_rate" step="0.01" min="0" inputMode="decimal" value={form.new_hourly_rate} onChange={handleChange}
                          className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholder="e.g. 12.50" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hours per Week *</label>
                      <input type="number" name="new_contracted_hours" step="0.5" min="0" max="168" inputMode="decimal" value={form.new_contracted_hours} onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="e.g. 37.5" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea name="notes" rows="3" value={form.notes} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Details about this contractual change..." />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm text-blue-700 dark:text-blue-300">
              This contractual change for {employee.first_name} {employee.last_name} will be included in the next payroll submission.
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Submit Change'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
